import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generateInvoiceFromSale, getTiposComprobante, getCondicionesIVA } from '../features/invoice/invoiceSlice';
import { getFiscalStatus } from '../features/fiscal/fiscalSlice';

const InvoiceFromSaleModal = ({ sale, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { tiposComprobante, condicionesIVA, loading } = useSelector((state) => state.invoice);
  const { fiscalStatus } = useSelector((state) => state.fiscal);

  const [formData, setFormData] = useState(() => {
    // Intentar extraer datos del cliente de la venta
    const c = sale.customer || {};
    // Si sale.customer es un ID (string), no tenemos los datos aquí (deberían venir poblados).
    // Si la venta tiene customerName/customerCuit (campos de snapshot), usarlos.
    // Asumiremos que si es objeto viene poblado, o usamos snapshots.

    // Fallback logic similar a Sales.jsx
    const docType = (c.cuit || c.tipo === 'CUIT') ? 80 : (c.dni || c.tipo === 'DNI') ? 96 : 99;

    return {
      saleId: sale._id,
      puntoVenta: '',
      tipoComprobante: 6, // Se ajustará luego según condición fiscal
      cliente: {
        tipoDocumento: docType,
        numeroDocumento: c.cuit || c.dni || c.numero || c.numeroDocumento || '0',
        razonSocial: c.razonSocial || c.name || c.nombre || sale.customerName || 'Consumidor Final',
        condicionIVA: typeof c.condicionIVA === 'number' ? c.condicionIVA : (c.tipoContribuyente ? Number(c.tipoContribuyente) : 5)
      }
    };
  });

  useEffect(() => {
    dispatch(getTiposComprobante());
    dispatch(getCondicionesIVA());
    dispatch(getFiscalStatus());
  }, [dispatch]);

  useEffect(() => {
    if (fiscalStatus?.puntosVenta && fiscalStatus.puntosVenta.length > 0) {
      setFormData(prev => ({
        ...prev,
        puntoVenta: fiscalStatus.puntosVenta[0].numero
      }));
    }
  }, [fiscalStatus]);

  // Detectar régimen fiscal del emisor para controlar opciones de comprobante
  const emisorCond = fiscalStatus?.condicionIVA ?? fiscalStatus?.condicion ?? fiscalStatus?.tipoResponsable;
  const isMonotributo = (() => {
    try {
      if (emisorCond == null) return false;
      if (Number(emisorCond) === 6) return true;
      if (typeof emisorCond === 'string' && /mono/i.test(emisorCond)) return true;
      return false;
    } catch (e) { return false; }
  })();
  const isResponsableInscripto = (() => {
    try {
      if (emisorCond == null) return false;
      if (Number(emisorCond) === 1) return true;
      if (typeof emisorCond === 'string' && /responsable inscripto/i.test(emisorCond)) return true;
      return false;
    } catch (e) { return false; }
  })();

  // Ajustar tipoComprobante por defecto según régimen fiscal
  useEffect(() => {
    try {
      if (isMonotributo) {
        setFormData(prev => ({ ...prev, tipoComprobante: 11 }));
        return;
      }
      if (isResponsableInscripto) {
        setFormData(prev => {
          const cur = Number(prev.tipoComprobante) || null;
          if (cur === 1 || cur === 6) return prev;
          return { ...prev, tipoComprobante: 6 };
        });
      }
    } catch (e) {
      // noop
    }
  }, [isMonotributo, isResponsableInscripto]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('cliente.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        cliente: {
          ...prev.cliente,
          [field]: value
        }
      }));
    } else {
      // mantener tipoComprobante como número
      const newVal = name === 'tipoComprobante' ? (Number(value) || value) : value;
      setFormData(prev => ({
        ...prev,
        [name]: newVal
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(generateInvoiceFromSale(formData)).then((result) => {
      if (!result.error) {
        onSuccess && onSuccess();
        onClose();
      }
    });
  };

  // Estilos
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const modalContentStyle = {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  };

  const buttonStyle = (variant = 'primary') => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: variant === 'primary'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : '#e2e8f0',
    color: variant === 'primary' ? '#ffffff' : '#2d3748'
  });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568'
  };

  const formGroupStyle = {
    marginBottom: '15px'
  };

  if (!fiscalStatus?.isConfigured) {
    return (
      <div style={modalOverlayStyle} onClick={onClose}>
        <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
            ⚠️ Configuración Requerida
          </h3>
          <p style={{ marginBottom: '20px', color: '#718096' }}>
            Debe configurar sus datos fiscales antes de generar facturas.
          </p>
          <button
            onClick={onClose}
            style={{ ...buttonStyle('secondary'), width: '100%' }}
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
          🧾 Generar Factura
        </h3>

        {/* Resumen de venta */}
        <div style={{
          padding: '15px',
          background: '#f7fafc',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div><strong>Venta #:</strong> {sale._id?.substring(0, 8)}...</div>
          <div><strong>Total:</strong> ${sale.total?.toFixed(2)}</div>
          <div><strong>Items:</strong> {sale.items?.length} producto(s)</div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Datos del comprobante */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Punto de Venta</label>
              {/* Punto de Venta fiscal fijo: no editable */}
              <select
                name="puntoVenta"
                value={formData.puntoVenta}
                onChange={handleChange}
                style={selectStyle}
                required
                disabled
              >
                {fiscalStatus?.puntosVenta?.map(pv => (
                  <option key={pv.numero} value={pv.numero}>
                    {String(pv.numero).padStart(5, '0')} - {pv.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Tipo de Comprobante</label>
              {/* Restringir opciones según régimen fiscal del emisor
                  - Monotributo: solo Factura C (11) y no editable
                  - Responsable Inscripto: solo Factura A (1) y B (6)
                  - Otros: mostrar tipos disponibles excluyendo notas de crédito */}
              <select
                name="tipoComprobante"
                value={formData.tipoComprobante}
                onChange={handleChange}
                style={selectStyle}
                required
                disabled={isMonotributo}
              >
                {(() => {
                  if (isMonotributo) {
                    // Force Factura C (11)
                    const label = tiposComprobante && (tiposComprobante['11'] || tiposComprobante[11]) ? (tiposComprobante['11'] || tiposComprobante[11]) : 'Factura C';
                    return <option key={11} value={11}>{label}</option>;
                  }

                  if (isResponsableInscripto) {
                    const allowed = ['1', '6'];
                    return Object.entries(tiposComprobante)
                      .filter(([k]) => allowed.includes(String(k)))
                      .map(([k, v]) => (<option key={k} value={k}>{v}</option>));
                  }

                  // Fallback: exclude obvious credit-note labels
                  return Object.entries(tiposComprobante)
                    .filter(([, v]) => {
                      try {
                        const txt = String(v || '').toLowerCase();
                        if (txt.includes('nota') && txt.includes('credito')) return false;
                        if (txt.includes('nota') && txt.includes('crédito')) return false;
                        return true;
                      } catch (e) { return true; }
                    })
                    .map(([k, v]) => (<option key={k} value={k}>{v}</option>));
                })()}
              </select>
            </div>
          </div>

          {/* Datos del cliente */}
          <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px' }}>
            📝 Datos del Cliente
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Condición IVA</label>
              <select
                name="cliente.condicionIVA"
                value={formData.cliente.condicionIVA}
                onChange={handleChange}
                style={selectStyle}
              >
                {Object.entries(condicionesIVA).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Razón Social / Nombre</label>
              <input
                type="text"
                name="cliente.razonSocial"
                value={formData.cliente.razonSocial}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Tipo Documento</label>
              <select
                name="cliente.tipoDocumento"
                value={formData.cliente.tipoDocumento}
                onChange={handleChange}
                style={selectStyle}
              >
                <option value="80">CUIT</option>
                <option value="86">CUIL</option>
                <option value="96">DNI</option>
                <option value="99">Sin identificar</option>
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Número</label>
              <input
                type="text"
                name="cliente.numeroDocumento"
                value={formData.cliente.numeroDocumento}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              type="submit"
              style={{ ...buttonStyle('primary'), flex: 1 }}
              disabled={loading}
            >
              {loading ? '⏳ Generando...' : '💾 Generar Factura'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{ ...buttonStyle('secondary'), flex: 1 }}
            >
              ❌ Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceFromSaleModal;
